import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { UserCheck, ArrowUpRight, ArrowDownLeft, Plus, X, Calendar, Users, AlertTriangle } from 'lucide-react';
import PageShell from '../../../components/leaves/PageShell';
import Button from '../../../components/ui/Button';
import { DelegationAssignmentView, DelegationAssignmentInput } from '../../../models/attendance';
import { UserView } from '../../../models/general';
import * as LeavesApi from '../../../api/Attendance/leaves';
import { FetchUsers } from '../../../api/General/account';
import { extractApiError } from '../../../utils/errors';

interface DelegationManagementProps {
  userId: string;
  company: { id: number };
  role: string;
}

type TabId = 'my-delegations' | 'delegated-to-me';

function getDelegationStatus(d: DelegationAssignmentView): 'active' | 'expired' | 'upcoming' | 'revoked' {
  if (!d.isActive) return 'revoked';
  const now = new Date();
  const start = new Date(d.startDate);
  const end = new Date(d.endDate);
  if (now < start) return 'upcoming';
  if (now > end) return 'expired';
  return 'active';
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

const statusConfig = {
  active: { label: 'Active', bgClass: 'bg-green-100 text-green-800', dotClass: 'bg-green-500' },
  expired: { label: 'Expired', bgClass: 'bg-gray-100 text-gray-600', dotClass: 'bg-gray-400' },
  upcoming: { label: 'Upcoming', bgClass: 'bg-blue-100 text-blue-800', dotClass: 'bg-blue-500' },
  revoked: { label: 'Revoked', bgClass: 'bg-red-100 text-red-700', dotClass: 'bg-red-500' },
};

const DelegationManagement: React.FC<DelegationManagementProps> = ({ userId, company, role }) => {
  const { t } = useTranslation(['leaves', 'common']);

  const [activeTab, setActiveTab] = useState<TabId>('my-delegations');
  const [myDelegations, setMyDelegations] = useState<DelegationAssignmentView[]>([]);
  const [delegationsToMe, setDelegationsToMe] = useState<DelegationAssignmentView[]>([]);
  const [users, setUsers] = useState<UserView[]>([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  // Create delegation form state
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState<DelegationAssignmentInput>({
    delegateId: '',
    startDate: '',
    endDate: '',
    reason: '',
  });
  const [submitting, setSubmitting] = useState(false);

  // Revoke confirmation
  const [revokeTarget, setRevokeTarget] = useState<DelegationAssignmentView | null>(null);

  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [myRes, toMeRes, usersRes] = await Promise.all([
        LeavesApi.getMyDelegations(),
        LeavesApi.getDelegationsToMe(),
        company?.id ? FetchUsers('Company', company.id) : Promise.resolve({ errors: [], payload: [] as UserView[] }),
      ]);
      if (myRes.errors.length === 0) setMyDelegations(myRes.payload);
      if (toMeRes.errors.length === 0) setDelegationsToMe(toMeRes.payload);
      if (usersRes.errors.length === 0) {
        setUsers(usersRes.payload.filter((u: UserView) => u.id !== userId && !u.isArchived));
      }
    } catch {
      showToast(t('Error loading data'), 'error');
    }
    setLoading(false);
  }, [company?.id, userId, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreateDelegation = async () => {
    if (!formData.delegateId || !formData.startDate || !formData.endDate) return;
    setSubmitting(true);
    try {
      const result = await LeavesApi.createDelegation(formData);
      if (result.errors.length === 0) {
        showToast(t('delegationCreated'), 'success');
        setShowForm(false);
        setFormData({ delegateId: '', startDate: '', endDate: '', reason: '' });
        fetchData();
      } else {
        showToast(result.errors[0]?.message || t('Submission failed'), 'error');
      }
    } catch (err) {
      showToast(extractApiError(err, t('Submission failed')), 'error');
    }
    setSubmitting(false);
  };

  const handleRevoke = async () => {
    if (!revokeTarget) return;
    try {
      const result = await LeavesApi.revokeDelegation(revokeTarget.id);
      if (result.errors.length === 0) {
        showToast(t('delegationRevoked'), 'success');
        setRevokeTarget(null);
        fetchData();
      } else {
        showToast(result.errors[0]?.message || t('Submission failed'), 'error');
      }
    } catch (err) {
      showToast(extractApiError(err, t('Submission failed')), 'error');
    }
  };

  const tabs = [
    { id: 'my-delegations' as TabId, label: t('My Delegations'), icon: ArrowUpRight },
    { id: 'delegated-to-me' as TabId, label: t('Delegated to Me'), icon: ArrowDownLeft },
  ];

  const activeDelegations = myDelegations.filter(d => getDelegationStatus(d) === 'active').length;
  const activeDelegationsToMe = delegationsToMe.filter(d => getDelegationStatus(d) === 'active').length;

  if (loading) {
    return (
      <PageShell
        title={t('Delegation Management')}
        description={t('delegationDescription')}
        icon={UserCheck}
        accentColor="emerald"
        className="min-h-screen bg-background"
      >
        <div className="flex items-center justify-center py-24">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4" />
            <p className="text-muted-foreground">{t('common:Loading')}</p>
          </div>
        </div>
      </PageShell>
    );
  }

  const renderStatusBadge = (delegation: DelegationAssignmentView) => {
    const status = getDelegationStatus(delegation);
    const config = statusConfig[status];
    const label = t(config.label);
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full ${config.bgClass}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${config.dotClass}`} />
        {label}
      </span>
    );
  };

  const renderDelegationCard = (delegation: DelegationAssignmentView, showDelegate: boolean) => {
    const status = getDelegationStatus(delegation);
    const canRevoke = showDelegate && (status === 'active' || status === 'upcoming');

    return (
      <div
        key={delegation.id}
        data-testid={`delegation-card-${delegation.id}`}
        className="border border-border rounded-lg p-4 hover:shadow-sm transition-shadow bg-card"
      >
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-semibold ${
              status === 'active' ? 'bg-indigo-500' : status === 'upcoming' ? 'bg-blue-500' : 'bg-gray-400'
            }`}>
              {(showDelegate ? delegation.delegateName : delegation.managerName).charAt(0).toUpperCase()}
            </div>
            <div>
              <p className="font-medium text-foreground">
                {showDelegate ? delegation.delegateName : delegation.managerName}
              </p>
              <p className="text-xs text-muted-foreground">
                {showDelegate ? t('Delegate') : t('Delegated By')}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {renderStatusBadge(delegation)}
            {canRevoke && (
              <button
                onClick={() => setRevokeTarget(delegation)}
                data-testid={`revoke-delegation-${delegation.id}`}
                className="p-1.5 text-red-500 hover:bg-red-50 rounded-md transition-colors"
                title={t('Revoke')}
              >
                <X size={16} />
              </button>
            )}
          </div>
        </div>

        <div className="mt-3 flex items-center gap-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-1.5">
            <Calendar size={14} />
            <span>{formatDate(delegation.startDate)} — {formatDate(delegation.endDate)}</span>
          </div>
        </div>

        {delegation.reason && (
          <p className="mt-2 text-sm text-muted-foreground italic">
            "{delegation.reason}"
          </p>
        )}
      </div>
    );
  };

  const currentList = activeTab === 'my-delegations' ? myDelegations : delegationsToMe;
  const emptyMessage = activeTab === 'my-delegations' ? t('noDelegationsMessage') : t('noDelegationsToMeMessage');

  return (
    <PageShell
      title={t('Delegation Management')}
      description={t('delegationDescription')}
      icon={UserCheck}
      accentColor="emerald"
      maxWidth="5xl"
      stats={[
        { label: t('My Delegations'), value: myDelegations.length },
        { label: t('Active Given'), value: activeDelegations, color: 'text-green-600' },
        { label: t('Delegated to Me'), value: delegationsToMe.length },
        { label: t('Active Received'), value: activeDelegationsToMe, color: 'text-blue-600' },
      ]}
      headerActions={
        <Button
          variant="outline"
          onClick={() => setShowForm(true)}
          data-testid="create-delegation-btn"
          iconName="Plus"
        >
          {t('Create Delegation')}
        </Button>
      }
      className="min-h-screen bg-background"
    >
      {/* Tabs */}
      <div className="pt-6">
        <div className="flex gap-1 bg-muted p-1 rounded-lg w-fit" data-testid="delegation-tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`tab-${tab.id}`}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                activeTab === tab.id
                  ? 'bg-card text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <tab.icon size={16} />
              {tab.label}
              <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                activeTab === tab.id ? 'bg-indigo-100 text-indigo-700' : 'bg-muted-foreground/20'
              }`}>
                {tab.id === 'my-delegations' ? myDelegations.length : delegationsToMe.length}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="py-6">
        {currentList.length === 0 ? (
          <div className="bg-card border border-border rounded-xl p-12 text-center" data-testid="delegation-empty">
            <Users size={48} className="mx-auto mb-4 text-muted-foreground/40" />
            <p className="text-lg font-medium text-foreground">{t('No delegations')}</p>
            <p className="text-sm text-muted-foreground mt-1">{emptyMessage}</p>
            {activeTab === 'my-delegations' && (
              <button
                onClick={() => setShowForm(true)}
                className="mt-4 inline-flex items-center px-4 py-2 text-sm font-medium text-indigo-600 border border-indigo-200 rounded-lg hover:bg-indigo-50 transition-colors"
              >
                <Plus size={16} className="ltr:mr-1.5 rtl:ml-1.5" />
                {t('Create Delegation')}
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-3" data-testid="delegation-list">
            {currentList.map((d) => renderDelegationCard(d, activeTab === 'my-delegations'))}
          </div>
        )}
      </div>

      {/* Create Delegation Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" data-testid="create-delegation-modal">
          <div className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between p-5 border-b border-border">
              <h2 className="text-lg font-semibold text-foreground">{t('Create Delegation')}</h2>
              <button onClick={() => setShowForm(false)} data-testid="leave-delegation-modal-close" className="text-muted-foreground hover:text-foreground">
                <X size={20} />
              </button>
            </div>
            <div className="p-5 space-y-4">
              {/* Delegate selector */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">{t('Select Delegate')}</label>
                <select
                  value={formData.delegateId}
                  onChange={(e) => setFormData(prev => ({ ...prev, delegateId: e.target.value }))}
                  data-testid="delegate-select"
                  className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  <option value="">{t('selectDelegatePlaceholder')}</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.fullName || u.userName} {u.title ? `— ${u.title}` : ''}
                    </option>
                  ))}
                </select>
              </div>

              {/* Date range */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">{t('Start Date')}</label>
                  <input
                    type="date"
                    value={formData.startDate}
                    onChange={(e) => setFormData(prev => ({ ...prev, startDate: e.target.value }))}
                    data-testid="delegation-start-date"
                    className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">{t('End Date')}</label>
                  <input
                    type="date"
                    value={formData.endDate}
                    onChange={(e) => setFormData(prev => ({ ...prev, endDate: e.target.value }))}
                    data-testid="delegation-end-date"
                    className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Reason */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">{t('Reason')}</label>
                <textarea
                  value={formData.reason || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, reason: e.target.value }))}
                  data-testid="delegation-reason"
                  rows={2}
                  placeholder={t('delegationReasonPlaceholder')}
                  className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 p-5 border-t border-border">
              <button
                onClick={() => setShowForm(false)}
                className="px-4 py-2 text-sm font-medium text-foreground border border-border rounded-lg hover:bg-muted transition-colors"
              >
                {t('Cancel')}
              </button>
              <button
                onClick={handleCreateDelegation}
                disabled={submitting || !formData.delegateId || !formData.startDate || !formData.endDate}
                data-testid="submit-delegation-btn"
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {submitting ? t('common:Loading') : t('Create Delegation')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Revoke Confirmation Modal */}
      {revokeTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" data-testid="revoke-delegation-modal">
          <div className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-sm mx-4">
            <div className="p-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                  <AlertTriangle size={20} className="text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-foreground">{t('Revoke Delegation')}</h3>
              </div>
              <p className="text-sm text-muted-foreground">{t('revokeConfirmation')}</p>
              <div className="mt-3 p-3 bg-muted rounded-lg text-sm">
                <p className="font-medium text-foreground">{revokeTarget.delegateName}</p>
                <p className="text-muted-foreground">
                  {formatDate(revokeTarget.startDate)} — {formatDate(revokeTarget.endDate)}
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-3 p-5 border-t border-border">
              <button
                onClick={() => setRevokeTarget(null)}
                data-testid="leave-delegation-revoke-cancel"
                className="px-4 py-2 text-sm font-medium text-foreground border border-border rounded-lg hover:bg-muted transition-colors"
              >
                {t('Cancel')}
              </button>
              <button
                onClick={handleRevoke}
                data-testid="confirm-revoke-btn"
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
              >
                {t('Revoke')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-8 left-1/2 transform -translate-x-1/2 px-6 py-3 rounded-lg shadow-lg text-sm font-medium z-50 transition-all ${
          toast.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
        }`}>
          {toast.message}
        </div>
      )}
    </PageShell>
  );
};

const mapStateToProps = (state: any) => ({
  userId: state.user.userId,
  company: state.user.company,
  role: state.user.role,
});

export default connect(mapStateToProps)(DelegationManagement);
